import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Pagination from "../Pagination";
import SearchableSelect from "../SearchableSelect";
import { tableRows } from "../../utils/xlsx";
import {
  ACCOUNTING,
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  sheetHeading,
  todayIso,
  validPeriod,
} from "./common";

const VOUCHERS = `${ACCOUNTING}/vouchers`;
const PAGE_SIZE = 50;
const EXPORT_LIMIT = 5000;

// What each manually entered voucher type is for, and the rule the server enforces on it.
const MANUAL_TYPES = [
  { key: "PAYMENT", label: "Payment", hint: "Money paid out: expenses, supplier payments, loan repayments. Must credit a cash or bank ledger." },
  { key: "RECEIPT", label: "Receipt", hint: "Money received other than customer collections: capital, loans, other income. Must debit a cash or bank ledger." },
  { key: "CONTRA", label: "Contra", hint: "Transfers between cash and bank: deposits, withdrawals, bank-to-bank. Cash and bank ledgers only." },
  { key: "JOURNAL", label: "Journal", hint: "Adjustments without cash: depreciation, provisions, write-offs, scheme claims. No cash or bank ledgers." },
  { key: "CREDIT_NOTE", label: "Credit Note", hint: "Non-cash credit to a party. No cash or bank ledgers." },
  { key: "DEBIT_NOTE", label: "Debit Note", hint: "Non-cash debit to a party, e.g. a claim raised on a supplier. No cash or bank ledgers." },
];
const ALL_TYPES = ["SALES", "PURCHASE", "RECEIPT", "PAYMENT", "CONTRA", "JOURNAL", "CREDIT_NOTE", "DEBIT_NOTE"];
const SOURCES = ["MANUAL", "SALES_INVOICE", "SALES_CREDIT_NOTE", "SALES_DEBIT_NOTE", "PAYMENT_COLLECTION", "DELIVERY_COLLECTION", "CHEQUE_BOUNCE", "PURCHASE_INVOICE"];
const label = (code) => (code || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const newLine = (side = "DR") => ({ side, ledgerId: "", amount: "", purchaseInvoiceId: "", remarks: "" });
const emptyForm = () => ({ voucherType: "PAYMENT", voucherDate: todayIso(), referenceNumber: "", narration: "", lines: [newLine("DR"), newLine("CR")] });

const particulars = (voucher) => {
  const names = [...new Set((voucher.entries || []).map((e) => e.ledgerName))];
  return names.length <= 2 ? names.join(" / ") : `${names.slice(0, 2).join(" / ")} (+${names.length - 2} more)`;
};

/** Day book of every voucher in the books, and entry of manual vouchers (payment, receipt, contra, journal, notes). */
const Vouchers = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [voucherType, setVoucherType] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [ledgerId, setLedgerId] = useState("");
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [billsByBrand, setBillsByBrand] = useState({});

  const filterParams = () => ({
    fromDate: period.fromDate,
    toDate: period.toDate,
    voucherType: voucherType || undefined,
    sourceType: sourceType || undefined,
    ledgerId: ledgerId || undefined,
    includeCancelled,
    sort: "voucherDate,desc",
  });

  const load = async (targetPage = 0) => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setData(await api.get(VOUCHERS, { ...filterParams(), page: targetPage, size: PAGE_SIZE }));
      setPage(targetPage);
    } catch (error) {
      toast.error(error.message || "Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  };

  const loadLedgers = () =>
    api
      .get(`${ACCOUNTING}/ledgers`, { includeInactive: true })
      .then((result) => setLedgers(Array.isArray(result) ? result : []))
      .catch((error) => toast.error(error.message || "Failed to load ledgers"));

  useEffect(() => {
    // Ledgers first: listing them creates the system ledgers (cash, bank…) for a distributor opening accounting for the first time.
    loadLedgers().then(() => load(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ledgerById = useMemo(() => Object.fromEntries(ledgers.map((l) => [String(l.id), l])), [ledgers]);
  const allLedgerOptions = useMemo(() => ledgers.map((l) => ({ value: l.id, label: l.name, sublabel: l.groupLabel })), [ledgers]);
  const activeLedgerOptions = useMemo(
    () => ledgers.filter((l) => l.active).map((l) => ({ value: l.id, label: l.name, sublabel: l.groupLabel })),
    [ledgers]
  );

  const rows = data?.content || [];

  // ---- export ---------------------------------------------------------------------------------------------------

  const exportExcel = async () => {
    if (!validPeriod(period)) return;
    setExporting(true);
    try {
      const all = await api.get(VOUCHERS, { ...filterParams(), page: 0, size: EXPORT_LIMIT });
      const vouchers = all?.content || [];
      if ((all?.totalElements || 0) > vouchers.length)
        toast.warn(`Only the first ${vouchers.length} of ${all.totalElements} vouchers were exported — narrow the period for the rest.`);
      const heading = sheetHeading("Day Book", periodLabel(period));
      const lines = vouchers.flatMap((v) => v.entries.map((e) => ({ ...e, voucher: v })));
      download(`Day Book ${periodLabel(period)}`, [
        {
          name: "Vouchers",
          rows: [
            ...heading,
            ...tableRows(
              [
                { label: "Date", key: "voucherDate" },
                { label: "Voucher No", key: "voucherNumber" },
                { label: "Type", value: (v) => label(v.voucherType) },
                { label: "Source", value: (v) => label(v.sourceType) },
                { label: "Particulars", value: particulars },
                { label: "Reference", key: "referenceNumber" },
                { label: "Narration", key: "narration" },
                { label: "Amount", key: "totalDebit", format: "money" },
                { label: "Status", value: (v) => (v.cancelled ? "Cancelled" : "Posted") },
              ],
              vouchers,
              { totals: true }
            ),
          ],
          widths: [12, 24, 14, 20, 40, 20, 40, 16, 11],
        },
        {
          name: "Voucher lines",
          rows: [
            ...heading,
            ...tableRows(
              [
                { label: "Date", value: (l) => l.voucher.voucherDate },
                { label: "Voucher No", value: (l) => l.voucher.voucherNumber },
                { label: "Type", value: (l) => label(l.voucher.voucherType) },
                { label: "Ledger", key: "ledgerName" },
                { label: "Group", value: (l) => label(l.group) },
                { label: "Debit", key: "debit", format: "money" },
                { label: "Credit", key: "credit", format: "money" },
                { label: "Remarks", key: "remarks" },
                { label: "Status", value: (l) => (l.voucher.cancelled ? "Cancelled" : "Posted") },
              ],
              lines,
              { totals: true }
            ),
          ],
          widths: [12, 24, 14, 36, 22, 16, 16, 30, 11],
        },
      ]);
    } catch (error) {
      toast.error(error.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ---- voucher entry ---------------------------------------------------------------------------------------------

  const loadBills = async (brandId) => {
    if (!brandId || billsByBrand[brandId]) return;
    try {
      const result = await api.get("/api/v1/purchase-invoices", { brandId, status: "VERIFIED", size: 500 });
      setBillsByBrand((prev) => ({ ...prev, [brandId]: result?.content || [] }));
    } catch {
      setBillsByBrand((prev) => ({ ...prev, [brandId]: [] }));
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (voucher) => {
    setEditingId(voucher.id);
    setForm({
      voucherType: voucher.voucherType,
      voucherDate: voucher.voucherDate,
      referenceNumber: voucher.referenceNumber || "",
      narration: voucher.narration || "",
      lines: voucher.entries.map((e) => ({
        side: Number(e.debit) > 0 ? "DR" : "CR",
        ledgerId: String(e.ledgerId),
        amount: String(Number(e.debit) > 0 ? e.debit : e.credit),
        purchaseInvoiceId: e.purchaseInvoiceId ? String(e.purchaseInvoiceId) : "",
        remarks: e.remarks || "",
      })),
    });
    voucher.entries.forEach((e) => {
      const ledger = ledgerById[String(e.ledgerId)];
      if (ledger?.partyType === "SUPPLIER") loadBills(ledger.partyId);
    });
    setViewing(null);
    setShowForm(true);
  };

  const setLine = (index, patch) =>
    setForm((prev) => ({ ...prev, lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }));

  const onLineLedger = (index, value) => {
    setLine(index, { ledgerId: value, purchaseInvoiceId: "" });
    const ledger = ledgerById[String(value)];
    if (ledger?.partyType === "SUPPLIER") loadBills(ledger.partyId);
  };

  const totalOf = (side) => form.lines.filter((l) => l.side === side).reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const totalDebit = totalOf("DR");
  const totalCredit = totalOf("CR");
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;
  const typeInfo = MANUAL_TYPES.find((t) => t.key === form.voucherType);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lines = form.lines.filter((l) => l.ledgerId || Number(l.amount));
    if (lines.length < 2) return toast.error("A voucher needs at least one debit and one credit line");
    if (lines.some((l) => !l.ledgerId || !(Number(l.amount) > 0))) return toast.error("Every line needs a ledger and an amount greater than zero");
    if (difference !== 0) return toast.error(`Debits and credits differ by ₹${money(Math.abs(difference))}`);
    setSaving(true);
    try {
      const payload = {
        voucherType: form.voucherType,
        voucherDate: form.voucherDate,
        referenceNumber: form.referenceNumber.trim() || null,
        narration: form.narration.trim() || null,
        entries: lines.map((l) => ({
          ledgerId: Number(l.ledgerId),
          debit: l.side === "DR" ? Number(l.amount) : null,
          credit: l.side === "CR" ? Number(l.amount) : null,
          purchaseInvoiceId: l.side === "DR" && l.purchaseInvoiceId ? Number(l.purchaseInvoiceId) : null,
          remarks: l.remarks.trim() || null,
        })),
      };
      if (editingId) {
        await api.put(`${VOUCHERS}/${editingId}`, payload);
        toast.success("Voucher updated");
      } else {
        const created = await api.post(VOUCHERS, payload);
        toast.success(`Voucher ${created?.voucherNumber || ""} saved`);
      }
      setShowForm(false);
      setBillsByBrand({});
      load(editingId ? page : 0);
    } catch (error) {
      toast.error(error.message || "Failed to save voucher");
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const cancelVoucher = async (voucher) => {
    if (!window.confirm(`Cancel voucher ${voucher.voucherNumber}? Its entries will be removed from the books.`)) return;
    try {
      await api.post(`${VOUCHERS}/${voucher.id}/cancel`);
      toast.success("Voucher cancelled");
      setViewing(null);
      setBillsByBrand({});
      load(page);
    } catch (error) {
      toast.error(error.message || "Failed to cancel voucher");
    }
  };

  const isEditable = (voucher) => voucher.sourceType === "MANUAL" && !voucher.cancelled;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading vouchers..." />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Every voucher in the books. Sales, purchases, collections and notes are posted automatically from their documents; enter payments, other
        receipts, contra and journal vouchers here.
      </div>
      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Voucher
        </button>
        <PeriodFilter period={period} onChange={setPeriod} />
        <select value={voucherType} onChange={(e) => setVoucherType(e.target.value)} aria-label="Voucher type">
          <option value="">All types</option>
          {ALL_TYPES.map((type) => (
            <option key={type} value={type}>
              {label(type)}
            </option>
          ))}
        </select>
        <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} aria-label="Source">
          <option value="">All sources</option>
          {SOURCES.map((source) => (
            <option key={source} value={source}>
              {source === "MANUAL" ? "Manual entry" : label(source)}
            </option>
          ))}
        </select>
        <SearchableSelect
          value={ledgerId}
          onChange={setLedgerId}
          options={[{ value: "", label: "All ledgers" }, ...allLedgerOptions]}
          placeholder="All ledgers"
          style={{ minWidth: "220px" }}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
          <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />
          Show cancelled
        </label>
        <button className="primary" onClick={() => load(0)} disabled={loading}>
          {loading ? "Loading..." : "Apply"}
        </button>
        <ExportButton onClick={exportExcel} disabled={exporting || !rows.length} label={exporting ? "Exporting..." : "Export Excel"} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Type</th>
              <th>Source</th>
              <th style={{ textAlign: "left" }}>Particulars</th>
              <th style={cellRight}>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState>No vouchers for these filters</EmptyState>
                </td>
              </tr>
            ) : (
              rows.map((voucher) => (
                <tr key={voucher.id} style={voucher.cancelled ? { opacity: 0.55 } : undefined}>
                  <td>{voucher.voucherDate}</td>
                  <td>{voucher.voucherNumber}</td>
                  <td>{label(voucher.voucherType)}</td>
                  <td>
                    <span className={`badge ${voucher.sourceType === "MANUAL" ? "badge-blue" : "badge-gray"}`}>
                      {voucher.sourceType === "MANUAL" ? "Manual" : label(voucher.sourceType)}
                    </span>
                  </td>
                  <td style={{ textAlign: "left" }} title={voucher.narration || ""}>
                    {particulars(voucher)}
                  </td>
                  <td style={cellRight}>{money(voucher.totalDebit)}</td>
                  <td>
                    <span className={`badge ${voucher.cancelled ? "badge-red" : "badge-green"}`}>{voucher.cancelled ? "Cancelled" : "Posted"}</span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => setViewing(voucher)}>
                      View
                    </button>
                    {isEditable(voucher) ? (
                      <button className="secondary" onClick={() => openEdit(voucher)}>
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={data?.totalPages || 0} totalElements={data?.totalElements} onPageChange={load} />

      {viewing ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setViewing(null)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">
                {label(viewing.voucherType)} — {viewing.voucherNumber}
              </h3>
              <button className="close-btn" onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
            <div className="page-subtitle">
              Dated {viewing.voucherDate}
              {viewing.referenceNumber ? ` · Ref ${viewing.referenceNumber}` : ""} ·{" "}
              {viewing.sourceType === "MANUAL" ? "Entered manually" : `Posted from ${label(viewing.sourceType)} #${viewing.sourceId}`}
              {viewing.cancelled ? " · Cancelled" : ""}
            </div>
            {viewing.narration ? <div style={{ marginTop: "8px" }}>{viewing.narration}</div> : null}
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Ledger</th>
                    <th style={{ textAlign: "left" }}>Group</th>
                    <th style={cellRight}>Debit</th>
                    <th style={cellRight}>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {viewing.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ textAlign: "left" }}>
                        {entry.ledgerName}
                        {entry.remarks || entry.purchaseInvoiceId ? (
                          <div className="page-subtitle">
                            {[entry.purchaseInvoiceId ? `Against purchase bill #${entry.purchaseInvoiceId}` : null, entry.remarks].filter(Boolean).join(" · ")}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ textAlign: "left" }}>{label(entry.group)}</td>
                      <td style={cellRight}>{Number(entry.debit) ? money(entry.debit) : ""}</td>
                      <td style={cellRight}>{Number(entry.credit) ? money(entry.credit) : ""}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                      Total
                    </td>
                    <td style={{ ...cellRight, fontWeight: 700 }}>{money(viewing.totalDebit)}</td>
                    <td style={{ ...cellRight, fontWeight: 700 }}>{money(viewing.totalCredit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {isEditable(viewing) ? (
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => cancelVoucher(viewing)}>
                  Cancel Voucher
                </button>
                <button type="button" className="primary" onClick={() => openEdit(viewing)}>
                  Edit
                </button>
              </div>
            ) : viewing.sourceType !== "MANUAL" ? (
              <div className="page-subtitle" style={{ marginTop: "12px" }}>
                This voucher follows its source document. To change it, edit or cancel that document.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {showForm ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal-box" style={{ maxWidth: "980px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Voucher" : "New Voucher"}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Voucher Type</label>
                  <select value={form.voucherType} onChange={(e) => setForm((prev) => ({ ...prev, voucherType: e.target.value }))} disabled={Boolean(editingId)}>
                    {MANUAL_TYPES.map((type) => (
                      <option key={type.key} value={type.key}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    onClick={(e) => e.target.showPicker?.()}
                    value={form.voucherDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, voucherDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reference No (cheque / UTR / bill)</label>
                  <input value={form.referenceNumber} onChange={(e) => setForm((prev) => ({ ...prev, referenceNumber: e.target.value }))} maxLength={100} />
                </div>
                <div className="form-group">
                  <label>Narration</label>
                  <input value={form.narration} onChange={(e) => setForm((prev) => ({ ...prev, narration: e.target.value }))} maxLength={500} />
                </div>
              </div>
              {typeInfo ? <div className="page-subtitle" style={{ marginTop: "10px" }}>{typeInfo.hint}</div> : null}

              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "90px" }}>Dr / Cr</th>
                      <th style={{ textAlign: "left" }}>Ledger</th>
                      <th style={{ width: "150px" }}>Amount</th>
                      <th style={{ textAlign: "left" }}>Against Bill / Remarks</th>
                      <th style={{ width: "70px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((line, index) => {
                      const ledger = ledgerById[String(line.ledgerId)];
                      const supplierDebit = ledger?.partyType === "SUPPLIER" && line.side === "DR";
                      const bills = (supplierDebit && billsByBrand[ledger.partyId]) || [];
                      return (
                        <tr key={index}>
                          <td>
                            <select value={line.side} onChange={(e) => setLine(index, { side: e.target.value, purchaseInvoiceId: "" })} aria-label="Debit or credit">
                              <option value="DR">Dr</option>
                              <option value="CR">Cr</option>
                            </select>
                          </td>
                          <td style={{ textAlign: "left" }}>
                            <SearchableSelect value={line.ledgerId} onChange={(value) => onLineLedger(index, value)} options={activeLedgerOptions} placeholder="Select ledger..." />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.amount}
                              onChange={(e) => setLine(index, { amount: e.target.value })}
                              style={{ width: "100%", textAlign: "right" }}
                              aria-label="Amount"
                            />
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {supplierDebit ? (
                              <select
                                value={line.purchaseInvoiceId}
                                onChange={(e) => setLine(index, { purchaseInvoiceId: e.target.value })}
                                style={{ width: "100%", marginBottom: "4px" }}
                                aria-label="Against purchase bill"
                              >
                                <option value="">On account (no bill)</option>
                                {bills
                                  .filter((bill) => Number(bill.outstanding) > 0 || String(bill.id) === line.purchaseInvoiceId)
                                  .map((bill) => (
                                    <option key={bill.id} value={bill.id}>
                                      {bill.brandInvoiceNumber} · {bill.invoiceDate} · due ₹{money(bill.outstanding)}
                                    </option>
                                  ))}
                              </select>
                            ) : null}
                            <input
                              value={line.remarks}
                              onChange={(e) => setLine(index, { remarks: e.target.value })}
                              placeholder="Remarks (optional)"
                              maxLength={300}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            {form.lines.length > 2 ? (
                              <button
                                type="button"
                                className="secondary"
                                onClick={() => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }))}
                                title="Remove line"
                              >
                                ✕
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ textAlign: "left" }}>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              lines: [...prev.lines, { ...newLine(difference > 0 ? "CR" : "DR"), amount: difference ? String(Math.abs(difference)) : "" }],
                            }))
                          }
                        >
                          + Add line
                        </button>
                      </td>
                      <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>
                        Debit ₹{money(totalDebit)} · Credit ₹{money(totalCredit)}
                        {difference !== 0 ? (
                          <span style={{ color: "var(--color-danger)", marginLeft: "10px" }}>
                            Difference ₹{money(Math.abs(difference))} {difference > 0 ? "Dr" : "Cr"}
                          </span>
                        ) : totalDebit > 0 ? (
                          <span style={{ color: "var(--color-success)", marginLeft: "10px" }}>Balanced</span>
                        ) : null}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update Voucher" : "Save Voucher"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Vouchers;
